<template name="ProgressDisplay">
  <div
    :class="
      cn(
        'progress-display-wrapper relative z-50 flex h-[52px] items-center gap-2 rounded-xl bg-c1 p-2 px-3 text-sm font-medium text-white shadow-lg',
        containerClassName
      )
    "
  >
    <div class="flex shrink-0 items-center gap-0.5">
      <Icon v-if="icon" :icon="icon" width="24" height="24" class="text-gray-400" />
      <ribbon-icon v-else class="h-6 w-6 text-gray-400" />
      <span class="hidden whitespace-nowrap md:block">{{ label }}:</span>
    </div>
    <ProgressBar
      :progress="progressRef"
      :height="barHeight"
      :active-color="activeColor"
      :inactive-color="inactiveColor"
      class="min-w-0 grow"
    />
    <span class="w-[3ch] shrink-0">{{ progressRef }}%</span>
    <div v-if="buttonText" class="shrink-0">
      <button
        class="rounded-md border border-gray-700 bg-menuItemBg px-3 py-[6px] text-xs text-gray-400 transition-colors duration-150 hover:bg-gray-700"
        @click="handleButtonClick"
      >
        {{ buttonText }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts" name="ProgressDisplayScript">
import RibbonIcon from '@/assets/ribbon.svg'
import ProgressBar from '@/components/ui/progressBars/ProgressBar.vue'
import { ref, watch, computed, toRef } from 'vue'
import { useAssessmentPopupStore } from '@/stores/assessmentPopupState'
import { toast, ToastTypes } from '@/helpers/toast'
import { Icon } from '@iconify/vue'
import { cn } from '@/lib/utils'
import { useAssessmentProgress } from '@/composables/useAssessmentProgress'
import type { ProgressBarProps } from './ProgressBar.vue'

interface ProgressDisplayProps extends Partial<Omit<ProgressBarProps, 'progress' | 'height'>> {
  label: string
  icon?: string
  buttonText?: string
  barHeight?: number
  handsCurrentlyPlayed?: number
  withNotifications?: boolean
  containerClassName?: string
}

const props = withDefaults(defineProps<ProgressDisplayProps>(), {
  barHeight: 18,
  withNotifications: false,
  activeColor: 'bg-[#318AFF]',
  inactiveColor: 'bg-white/30'
})

const popupStore = useAssessmentPopupStore()

const { data: assessmentData, isLoading: assessmentLoading } = useAssessmentProgress(
  toRef(props, 'handsCurrentlyPlayed')
)
const progressRef = ref(assessmentData.value?.progress ?? 0)

const emit = defineEmits<{
  (e: 'progressUpdate', progress: number): void
}>()

// Watch for progress changes and emit updates
watch(assessmentData, () => {
  if (!assessmentLoading.value && assessmentData.value?.progress !== progressRef.value) {
    progressRef.value = assessmentData.value?.progress ?? 0
    emit('progressUpdate', progressRef.value)
  }
})

enum ProgressNotificationName {
  START = 'start',
  HALFWAY = 'halfway',
  ALMOST = 'almost'
}

const progressNotifications = [
  {
    key: ProgressNotificationName.START,
    start: 25,
    end: 50,
    message: `Nice start! You've completed 25% of your assessment - keep going to see your stats!`
  },
  {
    key: ProgressNotificationName.HALFWAY,
    start: 50,
    end: 90,
    message: `Halfway there! Keep going - your personalised Game Insights report awaits.`
  },
  {
    key: ProgressNotificationName.ALMOST,
    start: 90,
    end: 99,
    message: `Only a few hands left!`
  }
]

const progressNotificationsState = ref<{
  [key in ProgressNotificationName]: boolean
}>({
  start: false,
  halfway: false,
  almost: false
})

watch(
  progressRef,
  (currentProgress) => {
    if (!props.withNotifications || currentProgress === 0) {
      return
    }

    progressNotifications.forEach((notification) => {
      if (
        currentProgress >= notification.start &&
        currentProgress < notification.end &&
        !progressNotificationsState.value[notification.key]
      ) {
        toast({
          message: notification.message,
          icon: ToastTypes.SUCCESS,
          options: {
            toastClassName: 'assessment-toast'
          }
        })
        progressNotificationsState.value[notification.key] = true
      }
    })
  },
  { immediate: true }
)

const handleButtonClick = () => {
  popupStore.showAssessment()
}
</script>

<style lang="scss">
.assessment-toast {
  &.Vue-Toastification__toast {
    padding: 16px 16px;
    font-size: 14px;
    border-radius: 12px;
    background: #1f212e;
    max-width: 334px;
    display: flex;
    align-items: flex-start;
  }

  .toast__main {
    font-size: 14px !important;
    font-weight: 400;
    padding-right: 15px;
  }

  .toast__icon {
    display: none;
  }

  .Vue-Toastification__toast-component-body {
    display: flex;
    align-items: center;
    min-width: 100px;
  }

  .Vue-Toastification__progress-bar {
    background: #22af30;
    height: 3px;
  }

  .Vue-Toastification__close-button {
    opacity: 0.5;
  }
}
</style>
